import { Injectable } from '@angular/core';
import { MS } from 'src/consts';

import { JWT } from '../../utils';
import Empleado from '../models/Empleado';
import { APIService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService extends EventTarget {
    constructor(private api: APIService) {
        super();
    }

    ping(): Promise<boolean> {
        return new Promise(resolve => {
            this.api
                .get('/ping/')
                .then((res: any) => resolve(res.success))
                .catch((err: any) => resolve(false));
        });
    }

    async ensureConnetion() {
        // Se pregunta ANTES de esperar. Dormir un segundo de entrada retrasaba
        // el arranque en el caso normal —el servidor está vivo— y ensanchaba la
        // ventana durante la que la sesión aún no se sabía.
        while (!(await this.ping())) {
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        return true;
    }

    initializing = true;
    // Arranca en `false`. Cuando valía `true`, los guards de ruta —que leen esta
    // propiedad de forma SÍNCRONA— dejaban pasar antes de que `sessionCheck()`
    // hubiera terminado: con la sesión caducada la aplicación se abría igual,
    // las peticiones salían con un token muerto y quedaba vacía hasta recargar.
    authed = false;
    accessExpireTime = 0;
    refreshExpireTime = 0;
    accessRefreshTimeout: any = null;

    private resolveReady!: () => void;
    /**
     * Se resuelve cuando la PRIMERA comprobación de sesión ha terminado, sea
     * cual sea el resultado. Es lo que esperan los guards para no decidir a
     * ciegas.
     */
    readonly ready: Promise<void> = new Promise(resolve => {
        this.resolveReady = resolve;
    });
    private checkStarted = false;

    /**
     * Arranca la comprobación inicial si nadie la ha arrancado, y devuelve la
     * promesa que se resuelve al acabar. Que pueda lanzarla el propio guard
     * evita que la aplicación se quede colgada en blanco el día que
     * `AppComponent` deje de llamarla.
     */
    ensureChecked(): Promise<void> {
        if (!this.checkStarted) this.sessionCheck();
        return this.ready;
    }

    async sessionCheck(): Promise<boolean> {
        this.checkStarted = true;
        this.log('Initializing auth service');
        let authed = false;

        // El `finally` es lo que garantiza que `ready` se resuelva por todos los
        // caminos —incluido el de «no hay tokens» y el de una excepción—. Si
        // alguno no lo resolviera, los guards esperarían para siempre.
        try {
            const tokens = this.getLocalTokens();
            if (!tokens) return false;

            await this.ensureConnetion();
            const { access, refresh } = tokens;
            if (JWT.isExpired(access)) {
                this.log('Access token expired');
                if (JWT.isExpired(refresh)) {
                    this.log('Refresh token expired');
                    this.clearLocalTokens();
                } else {
                    this.log('Refresh token not expired, refreshing access token');
                    authed = await this.refreshTokens();
                }
            } else {
                this.log('Access token not expired, you are logged in');
                authed = true;
                this.accessExpireTime = JWT.getExpireTime(access);
                this.refreshExpireTime = JWT.getExpireTime(refresh);

                this.setActiveAuthToken(access);

                this.prepareScheduleRefresh();
            }

            // Antes devolvía `true` siempre que hubiera tokens, con lo que quien
            // mirara el resultado no podía distinguir una sesión viva de una
            // caducada.
            return authed;
        } finally {
            this.setAuthed(authed);
            this.log('Auth service initialized');
            this.resolveReady();
        }
    }

    triggerEvent(event: any, data: any = null) {
        const eventObject = new CustomEvent(event, { detail: data });
        this.dispatchEvent(eventObject);
    }

    setAuthed(authed: boolean) {
        if (authed === this.authed && !this.initializing) return;

        this.authed = authed;

        if (authed) {
            this.triggerEvent('authed');
        } else {
            this.triggerEvent('unauthed');
        }
        this.initializing = false;
    }

    refreshTokens(): Promise<boolean> {
        return new Promise(resolve => {
            const tokens = this.getLocalTokens();
            if (!tokens) return resolve(false);

            const { refresh } = tokens;
            this.api
                .post('/auth/token/refresh/', { refresh }, false)
                .then((res: any) => {
                    const { access, refresh } = res;
                    this.saveLocalTokens(access, refresh);
                    this.sessionCheck().then(() => resolve(true));
                    this.triggerEvent('refreshed');
                })
                .catch((err: any) => {
                    this.log('Error refreshing access token', err);
                    this.clearLocalTokens();
                    resolve(false);
                });
        });
    }

    refreshPreventionTime: number = MS.inHour * 4;
    prepareScheduleRefresh() {
        const nowMinusMargin = Date.now() + this.refreshPreventionTime;
        const accessTime = this.accessExpireTime - nowMinusMargin;
        const refreshTime = this.refreshExpireTime - nowMinusMargin;

        let timeout = Math.min(accessTime, refreshTime);
        if (timeout < 0) timeout = 0;
        this.scheduleRefresh(timeout);
    }

    scheduleRefresh(timeout: number) {
        if (timeout < 0) timeout = 0;
        this.log('Scheduling refresh in', timeout / 1000 / 60, 'minutes');
        if (this.accessRefreshTimeout) clearTimeout(this.accessRefreshTimeout);
        this.accessRefreshTimeout = setTimeout(() => {
            this.refreshTokens();
        }, timeout);
    }

    setActiveAuthToken(token: string) {
        localStorage.setItem('active_auth_token', token);
    }

    getLocalTokens() {
        const tokens = localStorage.getItem('tokens');
        if (!tokens) return;

        return JSON.parse(tokens);
    }

    getAccessToken() {
        const tokens = this.getLocalTokens();
        if (!tokens) return;

        return tokens.access;
    }

    getRefreshToken() {
        const tokens = this.getLocalTokens();
        if (!tokens) return;

        return tokens.refresh;
    }

    saveLocalTokens(access: string, refresh: string) {
        localStorage.setItem('tokens', JSON.stringify({ access, refresh }));
    }

    clearLocalTokens() {
        this.log('Clearing local tokens');
        localStorage.removeItem('tokens');
        // También el token activo: es el que `APIService` pone en la cabecera
        // `Authorization`. Si se queda, la siguiente petición sale firmada con
        // un token caducado y el backend responde 401 en lugar de que la
        // aplicación mande a la pantalla de acceso.
        localStorage.removeItem('active_auth_token');
    }

    authenticate(worker_uid: string, password: string) {
        return new Promise((resolve, reject) => {
            this.api
                .post('/auth/worker_login/', { worker_uid, password }, false)
                .then((res: any) => {
                    const { access, refresh } = res;
                    this.saveLocalTokens(access, refresh);
                    this.sessionCheck().then(() => resolve(true));
                })
                .catch((err: any) => {
                    let error = 'Error al iniciar sesión';
                    if (err.status === 401)
                        error = 'Usuario o contraseña incorrectos';
                    else if (err.status === 429) {
                        const seconds = err.error?.detail?.split(' ');
                        error =
                            '<strong>Demasiados intentos</strong><br>Espera <strong>' +
                            seconds[seconds.length - 2] +
                            ' segundos</strong> antes de volver a intentarlo';
                    }
                    reject(error);
                });
        });
    }

    listEmployees(): Promise<[boolean, Empleado[] | string | null]> {
        return new Promise(resolve => {
            this.api
                .get(`/auth/employee/list/`)
                .then((res: any) => {
                    const { success } = res;
                    if (!success) return resolve([false, null]);

                    const { data } = res;
                    resolve([true, data as Empleado[]]);
                })
                .catch((error: any) => {
                    this.log('Error while updating user', error);
                    return resolve([false, null]);
                });
        });
    }

    logout() {
        return new Promise(async resolve => {
            const tokens = this.getLocalTokens();
            setTimeout(() => {
                if (!tokens) return;
                this.api.post(
                    '/auth/token/blacklist/',
                    { refresh: tokens.refresh },
                    false
                );
            });
            // We don't care if the request fails, we just want to clear the tokens
            this.clearLocalTokens();
            await this.sessionCheck();
            resolve(true);
        });
    }

    log(...args: any[]) {
        console.debug('[AuthService]', ...args);
    }
}

export default AuthService;
